import React, { useState, useEffect } from 'react';
import {
  FiBarChart2,
  FiTrendingUp,
  FiTrendingDown,
  FiEye,
  FiPhone,
  FiCalendar
} from 'react-icons/fi';
import AdminLayout from '../../components/admin/layout/AdminLayout';
import { useAdminAuth } from '../../hooks/admin/useAdminAuth';
import { API_URL } from '../../services/api';

interface TopCompany {
  id: string;
  name: string;
  views: number;
  contacts: number;
  change_percent: number;
}

const CompanyAnalytics: React.FC = () => {
  const { token } = useAdminAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dateRange, setDateRange] = useState('7d');
  const [limit, setLimit] = useState(10);
  const [topCompanies, setTopCompanies] = useState<TopCompany[]>([]);

  const breadcrumbs = [
    { label: 'สถิติบริษัท', icon: FiBarChart2 }
  ];

  // Fetch top companies data
  useEffect(() => {
    const fetchTopCompanies = async () => {
      if (!token) return;

      setLoading(true);
      setError(null);

      try {
        
        const response = await fetch(
          `${API_URL}/api/admin/analytics/top-companies?limit=${limit}&date_range=${dateRange}`,
          {
            headers: { 'Authorization': `Bearer ${token}` }
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch top companies data');
        }

        const data = await response.json();
        setTopCompanies(data.data || []);
      } catch (err) {
        console.error('Error fetching top companies:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchTopCompanies();
  }, [token, dateRange, limit]);

  const handleDateRangeChange = (range: string) => {
    setDateRange(range);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
  };

  const renderTrendIcon = (change: number) => {
    if (change > 0) {
      return <FiTrendingUp className="h-4 w-4 text-green-500" />;
    } else if (change < 0) {
      return <FiTrendingDown className="h-4 w-4 text-red-500" />;
    }
    return <div className="h-4 w-4" />;
  };

  const formatChange = (change: number) => {
    const sign = change > 0 ? '+' : '';
    const color = change > 0 ? 'text-green-600' : change < 0 ? 'text-red-600' : 'text-gray-600';
    return (
      <span className={`text-sm font-medium ${color}`}>
        {sign}{change.toFixed(1)}%
      </span>
    );
  };

  const calculateConversionRate = (contacts: number, views: number) => {
    if (views === 0) return 0;
    return ((contacts / views) * 100).toFixed(1);
  };

  return (
    <AdminLayout
      title="สถิติบริษัท"
      breadcrumbs={breadcrumbs}
    >
      <div className="space-y-6">
        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            <p className="font-medium">เกิดข้อผิดพลาดในการโหลดข้อมูล</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {/* Header with Filters */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">บริษัทยอดนิยม</h2>
              <p className="text-sm text-gray-600 mt-1">
                อันดับบริษัทตามจำนวนการเข้าชมและการติดต่อ
              </p>
            </div>

            <div className="flex items-center gap-4">
              {/* Date Range Selector */}
              <div className="flex items-center gap-2">
                <FiCalendar className="h-4 w-4 text-gray-400" />
                <select
                  value={dateRange}
                  onChange={(e) => handleDateRangeChange(e.target.value)}
                  className="block border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="1d">วันนี้</option>
                  <option value="7d">7 วันที่แล้ว</option>
                  <option value="30d">30 วันที่แล้ว</option>
                  <option value="90d">90 วันที่แล้ว</option>
                  <option value="1y">1 ปีที่แล้ว</option>
                </select>
              </div>

              {/* Limit Selector */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-600">แสดง:</label>
                <select
                  value={limit}
                  onChange={(e) => handleLimitChange(Number(e.target.value))}
                  className="block border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm"
                >
                  <option value="5">5 อันดับ</option>
                  <option value="10">10 อันดับ</option>
                  <option value="20">20 อันดับ</option>
                  <option value="50">50 อันดับ</option>
                </select>
              </div>
            </div>
          </div>
        </div>

        {/* Top Companies Table */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    อันดับ
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    ชื่อบริษัท
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center justify-end gap-1">
                      <FiEye className="h-4 w-4" />
                      <span>การเข้าชม</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    <div className="flex items-center justify-end gap-1">
                      <FiPhone className="h-4 w-4" />
                      <span>การติดต่อ</span>
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Conversion Rate
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    การเปลี่ยนแปลง
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-3 text-gray-600">กำลังโหลดข้อมูล...</span>
                      </div>
                    </td>
                  </tr>
                ) : topCompanies.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                      ไม่พบข้อมูลบริษัท
                    </td>
                  </tr>
                ) : (
                  topCompanies.map((company, index) => (
                    <tr key={company.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center justify-center">
                          <span className={`
                            inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium
                            ${index === 0 ? 'bg-yellow-100 text-yellow-800' : ''}
                            ${index === 1 ? 'bg-gray-100 text-gray-800' : ''}
                            ${index === 2 ? 'bg-orange-100 text-orange-800' : ''}
                            ${index > 2 ? 'bg-blue-50 text-blue-700' : ''}
                          `}>
                            {index + 1}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 max-w-md truncate">
                          {company.name}
                        </div>
                        <div className="text-xs text-gray-500">
                          ID: {company.id.substring(0, 8)}...
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-medium text-gray-900">
                          {company.views.toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-medium text-gray-900">
                          {company.contacts.toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="text-sm font-medium text-gray-900">
                          {calculateConversionRate(company.contacts, company.views)}%
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1">
                          {renderTrendIcon(company.change_percent)}
                          {formatChange(company.change_percent)}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Summary Statistics */}
        {!loading && topCompanies.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">รวมการเข้าชม</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {topCompanies.reduce((sum, c) => sum + c.views, 0).toLocaleString()}
                  </p>
                </div>
                <FiEye className="h-8 w-8 text-blue-500" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">รวมการติดต่อ</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {topCompanies.reduce((sum, c) => sum + c.contacts, 0).toLocaleString()}
                  </p>
                </div>
                <FiPhone className="h-8 w-8 text-green-500" />
              </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Conversion Rate เฉลี่ย</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">
                    {calculateConversionRate(
                      topCompanies.reduce((sum, c) => sum + c.contacts, 0),
                      topCompanies.reduce((sum, c) => sum + c.views, 0)
                    )}%
                  </p>
                </div>
                <FiBarChart2 className="h-8 w-8 text-purple-500" />
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default CompanyAnalytics;
